import {Component, OnDestroy, OnInit} from '@angular/core';
import {ReplaySubject} from "rxjs";
import {InvoiceDocument} from "../../../../../shared/interface/invoice-document";
import {InvoiceDocumentService} from "../../../../../shared/services/invoice-document.service";
import {takeUntil} from "rxjs/operators";
import {ActivatedRoute, Router} from "@angular/router";
import {HttpErrorResponse} from "@angular/common/http";
import {MatSnackBar} from "@angular/material/snack-bar";
import {UserPermissions} from "../../../../../shared/interface/user-permissions";
import {AuthService} from "../../../../../shared/services/auth.service";
import {ConfirmDialogComponent} from "../../../../../shared/components/confirm-dialog/confirm-dialog.component";
import {MatDialog} from "@angular/material/dialog";

@Component({
  selector: 'app-invoice-document-details',
  templateUrl: './invoice-document-details.component.html',
  styleUrls: ['./invoice-document-details.component.scss']
})
export class InvoiceDocumentDetailsComponent implements OnInit, OnDestroy {
  invoiceDocument: ReplaySubject<InvoiceDocument> = new ReplaySubject(1);
  userPermissions: ReplaySubject<UserPermissions>;
  invoiceDocumentObject?: InvoiceDocument;
  invoiceDocumentId: string | null = null;
  markingPaid = false;


  deductionDisplayedColumns = [
    'number',
    'issueDate',
    'status',
    'net',
    'amount',
    'actions'
  ];


  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private auth: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private invoiceDocumentService: InvoiceDocumentService,
    private snack: MatSnackBar
  ) {
    this.userPermissions = auth.getUserPermissions();
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroyed$))
      .subscribe(params => {
        this.invoiceDocumentId = params.id;
        this.loadInvoiceDocument();
      });
  }

  loadInvoiceDocument(): void {
    if (!this.invoiceDocumentId) {
      return;
    }

    this.invoiceDocumentService.get(this.invoiceDocumentId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (invoiceDocument: InvoiceDocument) => {
          this.invoiceDocument.next(invoiceDocument);
          this.invoiceDocumentObject = invoiceDocument;
        },
        error: (error: HttpErrorResponse) => {
          this.snack.open('Nie udało się pobrać danych noty', error.status.toString(), {
            duration: 2000
          });

          this.router.navigate(['/noty']);
        }
      });
  }


  get isWithoutContract(): boolean {
    return !!this.invoiceDocumentObject && !this.invoiceDocumentObject.contract;
  }

  markPaid(): void {
    if (
      !this.invoiceDocumentId
      || !this.isWithoutContract
      || this.markingPaid
      || (this.invoiceDocumentObject?.remainingAmount ?? 0) <= 0
    ) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz oznaczyć notę jako zapłaconą?'},
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed || !this.invoiceDocumentId) {
          return;
        }

        this.markingPaid = true;

        this.invoiceDocumentService.markPaid(this.invoiceDocumentId)
          .pipe(takeUntil(this.destroyed$))
          .subscribe({
            next: (result) => {
              if (this.invoiceDocumentObject) {
                this.invoiceDocumentObject = {
                  ...this.invoiceDocumentObject,
                  remainingAmount: result.remainingAmount,
                  deductedAmount: result.deductedAmount,
                };

                this.invoiceDocument.next(this.invoiceDocumentObject);
              }

              this.markingPaid = false;
              this.snack.open('Nota została oznaczona jako zapłacona', '', {
                duration: 2000
              });
            },
            error: (error: HttpErrorResponse) => {
              this.markingPaid = false;
              this.snack.open(
                'Nie udało się oznaczyć noty jako zapłaconej',
                error.status.toString(),
                {duration: 2000}
              );
            }
          });
      });
  }

  downloadPdf(): void {
    this.invoiceDocumentService.downloadPdf(this.invoiceDocumentId);
  }

  downloadDeductionPdf(invoiceDocumentDeductionId: string): void {
    this.invoiceDocumentService.downloadDeductionPdf(invoiceDocumentDeductionId);
  }

  delete(): void {
    if (!this.invoiceDocumentId) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: { text: 'Czy na pewno chcesz usunąć notę?' },
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.invoiceDocumentService.delete(this.invoiceDocumentId)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto notę', '', {duration: 2000});
              this.router.navigate(['/noty']);
            },
            error: (error: HttpErrorResponse) => {
              this.snack.open('Nie można usunąć noty', error.status.toString(), {duration: 2000});
            }
          });
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
